/* eslint-disable max-lines */
/**
 * GraphQL Types for Internal Audit Domain
 * Updated to match the new OpenAPI schema from the upstream audit service
 *
 * These types represent the GraphQL data structure returned from the BFF,
 * eliminating the need for artificial domain type transformations.
 */

import { getContentDisplayText } from '@core/utils';

export interface GraphQLAuditItem {
  id: string;
  type: string;
  createdBy: string;
  createdAt: string;
  lastModifiedBy: string;
  lastModifiedAt: string;
  auditSessionId: string;
  status: string;
  position: number;
  decisionsCount: number;
  selectedDecisionId?: string;
  kustoReference: {
    communityId: string;
    subjectId: string;
    timestamp: string;
  };
  contentSnapshot: {
    audioFileUrl?: string;
    contentUrl?: string;
    transcription?: string;
    language?: string;
    topic?: string;
    contentType?: string;
    classificationType?: string;
    classificationResults?: Array<{ name: string; severity: number }>;
    metadata?: string;
    triggeredPolicies?: Array<{
      id: string;
      name: string;
      description?: string;
      isEnabled: boolean;
      labels: Array<{
        labelId: string;
        severity: number;
      }>;
    }>;
    nonTriggeredPolicies?: Array<{
      id: string;
      name: string;
      description?: string;
      communityId: string;
      isEnabled: boolean;
      labels: Array<{
        labelId: string;
        severity: number;
      }>;
    }>;
    slmModelVersion?: string;
    slmPromptVersion?: string;
    quicktxtModelVersion?: string;
  };
  decisions: Array<{
    id: string;
    type: string;
    createdBy: string;
    createdAt: string;
    lastModifiedBy: string;
    lastModifiedAt: string;
    auditItemId: string;
    auditSessionId: string;
    auditorId: string;
    auditorName: string;
    timestamp: string;
    updatedAt?: string;
    isSelected: boolean;
    selectedBy?: string;
    selectedAt?: string;
    isCurrentUser: boolean;
    languageCorrect: boolean;
    transcriptionCorrect: boolean;
    correctedTranscription?: string;
    ocrCorrect: boolean;
    correctedOcr?: string;
    originalLabels: Array<{ name: string; severity: number }>;
    correctedLabels: Array<{ name: string; severity: number }>;
    comments?: string;
    labelsCorrect?: boolean;
    policiesCorrect?: boolean;
    triggeredPolicyAssessments?: Array<{
      policyId: string;
      policyName: string;
      isCorrect: boolean;
      comments?: string;
    }>;
    nonTriggeredPolicyAssessments?: Array<{
      policyId: string;
      policyName: string;
      isCorrect: boolean;
      comments?: string;
    }>;
  }>;
}

// Helper type for classification results
export interface ClassificationResult {
  name: string;
  severity: number;
}

// Policy correctness assessment type
export interface PolicyCorrectnessAssessment {
  policyId: string;
  policyName: string;
  isCorrect: boolean;
  comments?: string;
}

// Type alias for GraphQL decision data
export type GraphQLAuditDecision = GraphQLAuditItem['decisions'][0];

// Composition entry type for sample statistics
export interface CompositionEntry {
  key: string;
  count: number;
  percentage: number;
}

// Sample statistics with composition data
export interface SampleStatistics {
  sampleSize: number;
  confidenceLevel: number;
  marginOfError: number;
  populationSize: number;
  labelCounts?: CompositionEntry[];
  topicCounts?: CompositionEntry[];
  languageCounts?: CompositionEntry[];
}

// Dataset composition type
export interface DatasetComposition {
  byTopic: CompositionEntry[];
  byLanguage: CompositionEntry[];
  byLabel: CompositionEntry[];
  bySeverity: CompositionEntry[];
  totalItems: number;
}

// Flexible session type that works with both domain and GraphQL types
export interface FlexibleAuditSession {
  id: string;
  type: string;
  createdBy: string;
  createdAt: string;
  lastModifiedBy: string;
  lastModifiedAt: string;
  name: string;
  description?: string;
  completedAt?: string;
  status: string;
  sampleParameters: {
    communityId: string;
    samplingParameters: {
      confidenceLevel: number;
      marginOfError: number;
      populationProportion: number;
      timeframe: {
        start: string;
        end: string;
      };
    };
    filters: {
      languages?: string[];
      topics?: string[];
      labels?: string[];
      labelSeverities?: number[];
      contentTypes?: string[];
    };
  };
  sampleStatistics: SampleStatistics;
  datasetComposition?: DatasetComposition;
  itemsCount: number;
  completedCount: number;
  skippedCount: number;
  pendingCount: number;
  auditItems?: GraphQLAuditItem[];
  auditItemsWithDecisions?: {
    items: GraphQLAuditItem[];
    totalCount: number;
    page: number;
    pageSize: number;
  };
}

// GraphQL Community type - matches the actual GraphQL response structure
export interface GraphQLCommunity {
  id: string;
  name: string;
  communityGroupId: string;
  logoUrl?: string;
  optionalMetadata?: {
    description?: string;
    pgRating?: string;
    contentGenres?: string[];
    contentStandardsUrl?: string;
    bigId?: string;
  } | null;
  auditSession?: FlexibleAuditSession;
}

// ===== PHASE 3: STRATEGIC TYPE MAPPING OPTIMIZATIONS =====

/**
 * Lightweight UI-Specific Types for Performance Optimization
 *
 * These types provide strategic mapping opportunities that offer clear
 * performance benefits without reintroducing the complexity we eliminated.
 */

// Lightweight type for list views - reduces payload by 60-70%
export interface AuditItemSummary {
  id: string;
  auditSessionId: string;
  status: string;
  position: number;
  decisionsCount: number;
  selectedDecisionId?: string;
  // Minimal content preview
  contentPreview: {
    text: string; // First 100 chars
    topic?: string;
    language?: string;
    contentType?: string;
  };
  // Simplified classification
  primaryLabels: string[]; // Top 3 labels only
  hasConflicts: boolean;
}

// Centralized progress summary to avoid redundant calculations
export interface SessionProgressSummary {
  totalItems: number;
  completedItems: number;
  skippedItems: number;
  remainingItems: number;
  completionPercentage: number;
  estimatedTimeRemaining?: number;
}

// Enhanced GraphQL types with computed properties (client-side only)
export interface EnhancedGraphQLAuditItem extends GraphQLAuditItem {
  // Computed properties (not fetched, calculated client-side)
  readonly contentPreview: string;
  readonly primaryLabels: string[];
  readonly hasConflicts: boolean;
  readonly isCompleted: boolean;
}

/**
 * Helper functions for computed properties
 *
 * These provide consistent calculations across components without
 * server-side mapping complexity.
 */
export const auditItemHelpers = {
  getContentPreview: (item: GraphQLAuditItem): string => {
    const text = item.contentSnapshot?.transcription;

    return text ? `${text.substring(0, 100)}...` : getContentDisplayText(undefined, item.contentSnapshot?.contentUrl, item.contentSnapshot?.audioFileUrl);
  },

  getPrimaryLabels: (item: GraphQLAuditItem): string[] => item.contentSnapshot?.classificationResults?.slice(0, 3).map(r => r.name) || [],

  hasConflicts: (item: GraphQLAuditItem): boolean =>
    item.decisions?.some(
      d =>
        d.correctedLabels?.length !== d.originalLabels?.length ||
        d.correctedLabels?.some((corrected, index) => corrected.name !== d.originalLabels?.[index]?.name)
    ) || false,

  isCompleted: (item: GraphQLAuditItem): boolean => item.status === 'completed' || item.decisionsCount > 0,

  getContentTopic: (item: GraphQLAuditItem): string => item.contentSnapshot?.topic || 'Unknown',

  getContentLanguage: (item: GraphQLAuditItem): string => item.contentSnapshot?.language?.toUpperCase() || 'UNKNOWN',

  getContentType: (item: GraphQLAuditItem): string => item.contentSnapshot?.contentType?.toUpperCase() || 'UNKNOWN',

  // Convert to lightweight summary for list views
  toSummary: (item: GraphQLAuditItem): AuditItemSummary => ({
    id: item.id,
    auditSessionId: item.auditSessionId,
    status: item.status,
    position: item.position,
    decisionsCount: item.decisionsCount,
    selectedDecisionId: item.selectedDecisionId,
    contentPreview: {
      text: auditItemHelpers.getContentPreview(item),
      topic: auditItemHelpers.getContentTopic(item),
      language: auditItemHelpers.getContentLanguage(item),
      contentType: auditItemHelpers.getContentType(item),
    },
    primaryLabels: auditItemHelpers.getPrimaryLabels(item),
    hasConflicts: auditItemHelpers.hasConflicts(item),
  }),
};

/**
 * Session progress calculation helpers
 *
 * Centralized logic to avoid redundant calculations across components.
 */
export const sessionProgressHelpers = {
  calculateProgress: (session: FlexibleAuditSession): SessionProgressSummary => {
    const totalItems = session.itemsCount || 0;
    const completedItems = session.completedCount || 0;
    const skippedItems = session.skippedCount || 0;
    const remainingItems = session.pendingCount || 0; // Use real API pending count
    const completionPercentage = totalItems > 0 ? Math.round(((completedItems + skippedItems) / totalItems) * 100) : 0;

    return {
      totalItems,
      completedItems,
      skippedItems,
      remainingItems,
      completionPercentage,
      estimatedTimeRemaining: remainingItems > 0 ? remainingItems * 2 : undefined, // 2 min per item estimate
    };
  },

  calculateProgressFromItems: (items: GraphQLAuditItem[]): SessionProgressSummary => {
    const totalItems = items.length;
    const completedItems = items.filter(item => item.status === 'completed').length;
    const skippedItems = items.filter(item => item.status === 'skipped').length;
    const remainingItems = totalItems - completedItems - skippedItems;
    const completionPercentage = totalItems > 0 ? Math.round(((completedItems + skippedItems) / totalItems) * 100) : 0;

    return {
      totalItems,
      completedItems,
      skippedItems,
      remainingItems,
      completionPercentage,
      estimatedTimeRemaining: remainingItems > 0 ? remainingItems * 2 : undefined,
    };
  },
};

// ===== ADDITIONAL GRAPHQL RESPONSE TYPES =====

/**
 * Additional GraphQL response types for specific operations
 * These were previously duplicated in adapters/types.ts and adapters/auditDecisionActionTypes.ts
 */

// GraphQL response types for queries
export interface GetAuditSessionsResponse {
  community: {
    id: string;
    name: string;
    communityGroupId: string;
    logoUrl?: string;
    optionalMetadata: {
      description?: string;
      pgRating?: string;
      contentGenres?: string[];
      contentStandardsUrl?: string;
      bigId?: string;
    };
    auditSessions: FlexibleAuditSession[];
  };
}

// GraphQL response types for mutations
export interface CreateAuditSessionResponse {
  createAuditSession: FlexibleAuditSession;
}

export interface CreateAuditSessionVariables {
  input: {
    name: string;
    description?: string;
    sampleParameters: {
      communityId: string;
      samplingParameters: {
        confidenceLevel: number;
        marginOfError: number;
        populationProportion: number;
        timeframe: {
          start: string;
          end: string;
        };
      };
      filters: {
        languages?: string[];
        topics?: string[];
        labels?: string[];
        labelSeverities?: number[];
        contentTypes?: string[];
      };
    };
  };
}

// Delete Audit Decision types
export interface DeleteAuditDecisionVariables {
  input: {
    decisionId: string;
    auditItemId: string;
    auditSessionId: string;
  };
}

export interface DeleteAuditDecisionResponse {
  deleteAuditDecision: {
    auditItem: GraphQLAuditItem;
  };
}

// Select Audit Decision types
export interface SelectAuditDecisionVariables {
  input: {
    decisionId: string;
    auditItemId: string;
    auditSessionId: string;
  };
}

export interface SelectAuditDecisionResponse {
  selectAuditDecision: {
    auditItem: GraphQLAuditItem;
  };
}

// Skip Audit Item types
export interface SkipAuditItemVariables {
  input: {
    itemId: string;
  };
}

export interface SkipAuditItemResponse {
  skipAuditItem: {
    item: GraphQLAuditItem;
  };
}

// Unskip Audit Item types
export interface UnskipAuditItemVariables {
  input: {
    itemId: string;
  };
}

export interface UnskipAuditItemResponse {
  unskipAuditItem: {
    item: GraphQLAuditItem;
  };
}

// Type alias for backward compatibility with existing hooks
export type GraphQLAuditItemWithDecisions = GraphQLAuditItem;
